// skipcq: JS-W1028
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  DIFF_ADDED,
  DIFF_REMOVED,
  diffComparisonText,
  listComparisonFieldChanges,
} from '../../utils/brewery/beerIngestionComparison';

const FIELD_CHANGE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 3,
      maxWidth: 420,
      p: 1.5,
    },
  },
  arrow: {
    sx: { color: 'background.paper' },
  },
};

// DESIGN.md semantic-error / semantic-success pairs. MUI's auto-derived
// error.light / success.light sit too close to error.dark / success.dark
// on the vintage palette (dark ink on a dark wash).
const DIFF_REMOVED_HIGHLIGHT = {
  bgcolor: '#f2dfdd',
  color: '#4a1f1b',
};

const DIFF_ADDED_HIGHLIGHT = {
  bgcolor: '#dfe8e3',
  color: '#1e3326',
};

const DIFF_SEGMENT_SX = {
  [DIFF_REMOVED]: {
    ...DIFF_REMOVED_HIGHLIGHT,
    textDecoration: 'line-through',
    borderRadius: 0.5,
    px: 0.25,
    fontWeight: 600,
  },
  [DIFF_ADDED]: {
    ...DIFF_ADDED_HIGHLIGHT,
    borderRadius: 0.5,
    px: 0.25,
    fontWeight: 600,
  },
};

function DiffSegment({ type, text }) {
  const highlightSx = DIFF_SEGMENT_SX[type];
  if (!highlightSx) {
    return <Box component="span">{text}</Box>;
  }
  return (
    <Box component="mark" data-diff-type={type} sx={highlightSx}>
      {text}
    </Box>
  );
}

DiffSegment.propTypes = {
  type: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function DiffValueRow({ label, segments }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="p"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
      >
        {segments.length === 0 ? '—' : segments.map((segment) => (
          <DiffSegment
            key={`${segment.type}-${segment.startOffset}`}
            type={segment.type}
            text={segment.text}
          />
        ))}
      </Typography>
    </Box>
  );
}

DiffValueRow.propTypes = {
  label: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    startOffset: PropTypes.number.isRequired,
  })).isRequired,
};

function FieldChangeTooltipBody({ change }) {
  const { priorSegments, proposedSegments } = diffComparisonText(
    change.prior,
    change.proposed,
  );
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {change.label}
      </Typography>
      <DiffValueRow label="Previous" segments={priorSegments} />
      <DiffValueRow label="Proposed" segments={proposedSegments} />
    </Box>
  );
}

FieldChangeTooltipBody.propTypes = {
  change: PropTypes.shape({
    label: PropTypes.string.isRequired,
    prior: PropTypes.string.isRequired,
    proposed: PropTypes.string.isRequired,
  }).isRequired,
};

function ComparisonFieldChangeChip({ change }) {
  return (
    <Tooltip
      arrow
      enterDelay={200}
      placement="bottom-start"
      slotProps={FIELD_CHANGE_TOOLTIP_SLOT_PROPS}
      title={<FieldChangeTooltipBody change={change} />}
    >
      <Chip
        size="small"
        variant="outlined"
        component="button"
        type="button"
        label={change.label}
        aria-label={`${change.label} changed from ${change.prior} to ${change.proposed}`}
        sx={{ cursor: 'help' }}
      />
    </Tooltip>
  );
}

ComparisonFieldChangeChip.propTypes = {
  change: PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    prior: PropTypes.string.isRequired,
    proposed: PropTypes.string.isRequired,
  }).isRequired,
};

export function ComparisonFieldChangeChips({ candidate, emptyLabel = '—' }) {
  const changes = listComparisonFieldChanges(candidate);
  if (changes.length === 0) {
    if (!emptyLabel) {
      return null;
    }
    return (
      <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {changes.map((change) => (
        <ComparisonFieldChangeChip key={change.field} change={change} />
      ))}
    </Box>
  );
}

ComparisonFieldChangeChips.propTypes = {
  candidate: PropTypes.object.isRequired,
  emptyLabel: PropTypes.string,
};

export default function ComparisonFieldChangesCell({ candidate }) {
  return (
    <TableCell>
      <ComparisonFieldChangeChips candidate={candidate} />
    </TableCell>
  );
}

ComparisonFieldChangesCell.propTypes = {
  candidate: PropTypes.object.isRequired,
};
